'use client';

import { pipe } from 'effect';
import { Icon } from '~/components/primitives';
import { HorizontalStatelessTabs } from '~/components/primitives/HorizontalTabs';
import { usePortalRouter } from '~/features/global/portal';
import { useI18n } from '~/features/i18n';
import useWishlistState from '~/features/wishlist/useWishlistState';
import useCartState from '../useCartState';
import { Badge } from '~/components/ui/badge';

export declare namespace CartOrWishlist_Tabs {
    export type Tab = 'wishlist' | 'cart';
}
export const CartOrWishlist_Tabs: React.FC<{
    tab: CartOrWishlist_Tabs.Tab;
}> = props => {
    const i18n = useI18n();
    const router = usePortalRouter();
    const wishlist = useWishlistState();
    const cart = useCartState().useCartQuery();

    return (
        <HorizontalStatelessTabs<CartOrWishlist_Tabs.Tab>
            className="mb-8"
            size="small"
            tabs={[
                { label: i18n._('Wishlist'), value: 'wishlist' as const },
                { label: i18n._('Cart'), value: 'cart' as const },
            ]}
            value={props.tab}
            onChange={v =>
                matchTab(v, {
                    cart: () => router.push(`/cart`),
                    wishlist: () => router.push(`/wishlist`),
                })
            }
            renderTab={({ tab, isActive }) => (
                <>
                    <Icon
                        name={matchTab(tab.value, {
                            wishlist: () => 'heart',
                            cart: () => 'bag',
                        })}
                        size="sm"
                    />
                    <span>{tab.label} </span>

                    {pipe(
                        matchTab<number>(tab.value, {
                            cart: () => cart.data?.items.length ?? 0,
                            wishlist: () => wishlist.items.length ?? 0,
                        }),
                        count =>
                            count !== 0 && (
                                <Badge
                                    variant={isActive ? 'secondary' : 'passive'}
                                    className="rounded-full text-xs"
                                >
                                    {tab.value === 'wishlist'
                                        ? wishlist.items.length
                                        : (cart.data?.items.length ?? 0)}
                                </Badge>
                            ),
                    )}
                </>
            )}
        />
    );
};

function matchTab<T>(
    tab: CartOrWishlist_Tabs.Tab,
    matchers: Record<CartOrWishlist_Tabs.Tab, () => T>,
): T {
    return matchers[tab]();
}
